import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { REFERENCE_DATA_PAGE_SIZE } from "../../constants/pagination";
import { ticketsAPI } from "../../services/api";

const referenceListParams = { page_size: REFERENCE_DATA_PAGE_SIZE };

// Async thunks for loading reference data
export const loadCommunicationMethods = createAsyncThunk(
  "referenceData/loadCommunicationMethods",
  async (_, { rejectWithValue }) => {
    try {
      const response = await ticketsAPI.getCommunicationMethods();
      return response.results || response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const loadContactReasons = createAsyncThunk(
  "referenceData/loadContactReasons",
  async (_, { rejectWithValue }) => {
    try {
      const response = await ticketsAPI.getContactReasons();
      return response.results || response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const loadShortSubjects = createAsyncThunk(
  "referenceData/loadShortSubjects",
  async (_, { rejectWithValue }) => {
    try {
      const response = await ticketsAPI.getShortSubjects();
      return response.results || response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const loadBranches = createAsyncThunk(
  "referenceData/loadBranches",
  async (_, { rejectWithValue }) => {
    try {
      const response = await ticketsAPI.getBranches(referenceListParams);
      return response.results || response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const loadProducts = createAsyncThunk(
  "referenceData/loadProducts",
  async (_, { rejectWithValue }) => {
    try {
      const response = await ticketsAPI.getProducts(referenceListParams);
      return response.results || response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const loadDepartments = createAsyncThunk(
  "referenceData/loadDepartments",
  async (_, { rejectWithValue }) => {
    try {
      const response = await ticketsAPI.getDepartments(referenceListParams);
      return response.results || response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const loadCustomers = createAsyncThunk(
  "referenceData/loadCustomers",
  async (_, { rejectWithValue }) => {
    try {
      const response = await ticketsAPI.getCustomers(referenceListParams);
      return response.results || response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Load all reference data at once
export const loadAllReferenceData = createAsyncThunk(
  "referenceData/loadAllReferenceData",
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const results = await Promise.allSettled([
        dispatch(loadCommunicationMethods()),
        dispatch(loadContactReasons()),
        dispatch(loadShortSubjects()),
        dispatch(loadBranches()),
        dispatch(loadProducts()),
        dispatch(loadDepartments()),
        dispatch(loadCustomers()),
      ]);

      // Check if any requests failed
      const failedRequests = results.filter(
        (result) => result.status === "rejected"
      );
      if (failedRequests.length > 0) {
        console.warn("Some reference data failed to load:", failedRequests);
      }

      return results;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  // Data
  communicationMethods: [],
  contactReasons: [],
  shortSubjects: [],
  branches: [],
  products: [],
  departments: [],
  customers: [],

  // Loading states
  isLoading: false,
  isCommunicationMethodsLoading: false,
  isContactReasonsLoading: false,
  isShortSubjectsLoading: false,
  isBranchesLoading: false,
  isProductsLoading: false,
  isDepartmentsLoading: false,
  isCustomersLoading: false,

  // Error states
  error: null,
  communicationMethodsError: null,
  contactReasonsError: null,
  shortSubjectsError: null,
  branchesError: null,
  productsError: null,
  departmentsError: null,
  customersError: null,

  // Last loaded timestamps
  lastLoaded: {
    communicationMethods: null,
    contactReasons: null,
    shortSubjects: null,
    branches: null,
    products: null,
    departments: null,
    customers: null,
  },
};

const referenceDataSlice = createSlice({
  name: "referenceData",
  initialState,
  reducers: {
    clearReferenceData: (state) => {
      state.communicationMethods = [];
      state.contactReasons = [];
      state.shortSubjects = [];
      state.branches = [];
      state.products = [];
      state.departments = [];
      state.customers = [];
      state.error = null;
      state.communicationMethodsError = null;
      state.contactReasonsError = null;
      state.shortSubjectsError = null;
      state.branchesError = null;
      state.productsError = null;
      state.departmentsError = null;
      state.customersError = null;
      state.lastLoaded = {
        communicationMethods: null,
        contactReasons: null,
        shortSubjects: null,
        branches: null,
        products: null,
        departments: null,
        customers: null,
      };
    },
    clearErrors: (state) => {
      state.error = null;
      state.communicationMethodsError = null;
      state.contactReasonsError = null;
      state.shortSubjectsError = null;
      state.branchesError = null;
      state.productsError = null;
      state.departmentsError = null;
      state.customersError = null;
    },
    addCustomer: (state, action) => {
      // Add new customer to the beginning of the list
      state.customers.unshift(action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      // Load all reference data
      .addCase(loadAllReferenceData.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loadAllReferenceData.fulfilled, (state) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(loadAllReferenceData.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Communication Methods
      .addCase(loadCommunicationMethods.pending, (state) => {
        state.isCommunicationMethodsLoading = true;
        state.communicationMethodsError = null;
      })
      .addCase(loadCommunicationMethods.fulfilled, (state, action) => {
        state.isCommunicationMethodsLoading = false;
        state.communicationMethods = action.payload;
        state.communicationMethodsError = null;
        state.lastLoaded.communicationMethods = Date.now();
      })
      .addCase(loadCommunicationMethods.rejected, (state, action) => {
        state.isCommunicationMethodsLoading = false;
        state.communicationMethodsError = action.payload;
      })

      // Contact Reasons
      .addCase(loadContactReasons.pending, (state) => {
        state.isContactReasonsLoading = true;
        state.contactReasonsError = null;
      })
      .addCase(loadContactReasons.fulfilled, (state, action) => {
        state.isContactReasonsLoading = false;
        state.contactReasons = action.payload;
        state.contactReasonsError = null;
        state.lastLoaded.contactReasons = Date.now();
      })
      .addCase(loadContactReasons.rejected, (state, action) => {
        state.isContactReasonsLoading = false;
        state.contactReasonsError = action.payload;
      })

      // Short Subjects
      .addCase(loadShortSubjects.pending, (state) => {
        state.isShortSubjectsLoading = true;
        state.shortSubjectsError = null;
      })
      .addCase(loadShortSubjects.fulfilled, (state, action) => {
        state.isShortSubjectsLoading = false;
        state.shortSubjects = action.payload;
        state.shortSubjectsError = null;
        state.lastLoaded.shortSubjects = Date.now();
      })
      .addCase(loadShortSubjects.rejected, (state, action) => {
        state.isShortSubjectsLoading = false;
        state.shortSubjectsError = action.payload;
      })

      // Branches
      .addCase(loadBranches.pending, (state) => {
        state.isBranchesLoading = true;
        state.branchesError = null;
      })
      .addCase(loadBranches.fulfilled, (state, action) => {
        state.isBranchesLoading = false;
        state.branches = action.payload;
        state.branchesError = null;
        state.lastLoaded.branches = Date.now();
      })
      .addCase(loadBranches.rejected, (state, action) => {
        state.isBranchesLoading = false;
        state.branchesError = action.payload;
      })

      // Products
      .addCase(loadProducts.pending, (state) => {
        state.isProductsLoading = true;
        state.productsError = null;
      })
      .addCase(loadProducts.fulfilled, (state, action) => {
        state.isProductsLoading = false;
        state.products = action.payload;
        state.productsError = null;
        state.lastLoaded.products = Date.now();
      })
      .addCase(loadProducts.rejected, (state, action) => {
        state.isProductsLoading = false;
        state.productsError = action.payload;
      })

      // Departments
      .addCase(loadDepartments.pending, (state) => {
        state.isDepartmentsLoading = true;
        state.departmentsError = null;
      })
      .addCase(loadDepartments.fulfilled, (state, action) => {
        state.isDepartmentsLoading = false;
        state.departments = action.payload;
        state.departmentsError = null;
        state.lastLoaded.departments = Date.now();
      })
      .addCase(loadDepartments.rejected, (state, action) => {
        state.isDepartmentsLoading = false;
        state.departmentsError = action.payload;
      })

      // Customers
      .addCase(loadCustomers.pending, (state) => {
        state.isCustomersLoading = true;
        state.customersError = null;
      })
      .addCase(loadCustomers.fulfilled, (state, action) => {
        state.isCustomersLoading = false;
        state.customers = action.payload;
        state.customersError = null;
        state.lastLoaded.customers = Date.now();
      })
      .addCase(loadCustomers.rejected, (state, action) => {
        state.isCustomersLoading = false;
        state.customersError = action.payload;
      });
  },
});

export const { clearReferenceData, clearErrors, addCustomer } =
  referenceDataSlice.actions;

// Selectors
export const selectCommunicationMethods = (state) =>
  state.referenceData.communicationMethods;
export const selectContactReasons = (state) =>
  state.referenceData.contactReasons;
export const selectShortSubjects = (state) => state.referenceData.shortSubjects;
export const selectBranches = (state) => state.referenceData.branches;
export const selectProducts = (state) => state.referenceData.products;
export const selectDepartments = (state) => state.referenceData.departments;
export const selectCustomers = (state) => state.referenceData.customers;

export const selectIsReferenceDataLoading = (state) =>
  state.referenceData.isLoading;
export const selectReferenceDataError = (state) => state.referenceData.error;

export const selectIsCommunicationMethodsLoading = (state) =>
  state.referenceData.isCommunicationMethodsLoading;
export const selectIsContactReasonsLoading = (state) =>
  state.referenceData.isContactReasonsLoading;
export const selectIsShortSubjectsLoading = (state) =>
  state.referenceData.isShortSubjectsLoading;
export const selectIsBranchesLoading = (state) =>
  state.referenceData.isBranchesLoading;
export const selectIsProductsLoading = (state) =>
  state.referenceData.isProductsLoading;
export const selectIsDepartmentsLoading = (state) =>
  state.referenceData.isDepartmentsLoading;
export const selectIsCustomersLoading = (state) =>
  state.referenceData.isCustomersLoading;

export const selectReferenceDataErrors = (state) => ({
  communicationMethods: state.referenceData.communicationMethodsError,
  contactReasons: state.referenceData.contactReasonsError,
  shortSubjects: state.referenceData.shortSubjectsError,
  branches: state.referenceData.branchesError,
  products: state.referenceData.productsError,
  departments: state.referenceData.departmentsError,
  customers: state.referenceData.customersError,
});

export default referenceDataSlice.reducer;
